// eslint-disable-next-line
// @ts-nocheck
import { SvgManager } from 'core';
import Tabs from '../../components/tabs';
import { itemType as itemTypeMetadata } from 'metadata';
import { wire } from 'hyperhtml';
import getLayoutByName from './layoutLoader';
import type { Tab } from '../../components/tabs';
import type TocEditorCuiLayout from '../../mainWindowCuiLayout/TocEditor/TocEditorCuiLayout';
import type { Favorite } from '../../store/storeTypes';
import type MainWindowCuiActions from '../../mainWindowCuiLayout/MainWindowCuiActions';
import * as Inferno from 'inferno';

type TabTypes = {
	Form: {
		formId: string;
	};
	Page: {
		url: string;
	};
	CuiLayout: {
		layoutName: string;
		location: string;
		layoutId: string;
		layout: TocEditorCuiLayout;
	};
	Search: {
		itemTypeId: string;
		favoriteSearchId?: Favorite['id'];
	};
	Item: {
		itemTypeId: string;
		itemId: string;
	};
};

export type GHeaderTab<T extends keyof TabTypes> = Tab & TabTypes[T];

export type HeaderTab = GHeaderTab<keyof TabTypes>;

const icons = {
	defaultItemType: '../images/DefaultItemType.svg',
	gridSearch: '../images/GridSearch.svg'
};

SvgManager.enqueue(Object.values(icons));

const IFRAME_CSS_CLASS = 'content-block__iframe';
const PAGE_IFRAME_CSS_CLASS = 'content-block__iframe_page';

const selectTab = (
	tabs: HeaderTabs,
	tabId: string,
	preventFocus = false
): Promise<void> => {
	const previousSelectedId = tabs.selectedTab;
	const renderPromise = tabs.selectTab(tabId, preventFocus);
	const event = new CustomEvent('select', {
		detail: {
			id: tabs.selectedTab,
			previousId: previousSelectedId
		}
	});
	tabs.dispatchEvent(event);

	return renderPromise;
};

export default class HeaderTabs extends Tabs {
	declare data: Map<string, HeaderTab>;
	options: {
		updateTabState?:
			| ReturnType<typeof MainWindowCuiActions>['updateDockedTabs']
			| ReturnType<typeof MainWindowCuiActions>['updateUndockedTabs'];
	} = {};
	initialized = false;
	_dropdownBox: null | HTMLElement = null;
	_frameCssClass: null | string = null;

	connectedCallback() {
		if (!this.initialized) {
			this.html`
				<div class="aras-tabs aras-tabs_a aras-flex-grow">
					<span class="aras-tabs-arrow"></span>
					<div class="aras-tabs__list-container">
					</div>
					<span class="aras-tabs-arrow"></span>
				</div>
				<aras-dropdown closeonclick position="bottom-right" class="aras-dropdown-container">
					<div class="tabs-button" dropdown-button></div>
					<div class="aras-dropdown">
					</div>
				</aras-dropdown>
			`;

			const dropdownContainer = this.querySelector('.aras-dropdown-container');
			dropdownContainer?.addEventListener('dropdownbeforeopen', () => {
				this.dropdownTabRender();
			});

			this._dropdownBox =
				dropdownContainer?.querySelector('div.aras-dropdown') || null;
			this._frameCssClass = IFRAME_CSS_CLASS;
			this._elem = this.firstElementChild;
			this._movable = this._elem.querySelector('div');

			this.makeScroll();
			this.makeSelectable();
			this.makeDraggable();
			this.connectKeyboard();

			this.initialized = true;
		}
		this.render();
	}
	openForm(formId: string, icon: string, label: string) {
		if (!formId) {
			return null;
		}
		const formName = aras.MetadataCache.GetFormName(formId);
		const tab = this.tabs.find(function (tab) {
			return tab.indexOf('form_' + formId + formName + '_') !== -1;
		});
		if (tab) {
			this.selectTab(tab);
			return;
		}
		const url = aras.getScriptsURL(
			'ShowFormInFrame.html?formId=' + formId + '&formType=edit&item=undefined'
		);
		const winName = 'form_' + formId + formName + '_' + Date.now();
		const win = this.open(url, winName, false, PAGE_IFRAME_CSS_CLASS);
		this.updateTitleTab(winName, { formId, label, image: icon });

		return win;
	}
	openPage(url: undefined | string, icon: string, label: string) {
		if (!url) {
			return null;
		}

		const tab = this.tabs.find(function (tab) {
			return tab.indexOf('page_' + url) !== -1;
		});
		if (tab) {
			this.selectTab(tab);
			return;
		}
		const winName = 'page_' + url + '_' + Date.now();
		const pageUrl = aras.getScriptsURL(url);
		const win = this.open(pageUrl, winName, false, PAGE_IFRAME_CSS_CLASS);
		this.updateTitleTab(winName, { startPage: url, label, image: icon });

		return win;
	}
	async openCuiLayout(
		layoutName: string,
		location: string,
		layoutId: string,
		{
			label,
			image,
			closable = true
		}: { label?: string; image?: string; closable?: boolean }
	) {
		const tabId = 'layout_' + layoutId;
		const hasTab = this.tabs.includes(tabId);
		if (hasTab) {
			await selectTab(this, tabId);
			return;
		}

		this.addTab(tabId, {
			layoutName,
			location,
			layoutId,
			label,
			image,
			closable: false
		});

		const containerNode = wire()`
			<div
				role="tabpanel"
				class="content-block__tab"
				id="${tabId}"
			/>
		`;
		const tabsContainer = document.getElementById('center');
		tabsContainer?.appendChild(containerNode);

		const LayoutConstructor = await getLayoutByName(layoutName);
		const layout = new LayoutConstructor(containerNode, location);

		await Promise.all([
			layout.init(),
			this.setTabContent(tabId, { layout, closable }),
			selectTab(this, tabId)
		]);

		return tabId;
	}
	openSearch(itemTypeId: string, favoriteSearchId?: string) {
		const itemTypeName = itemTypeMetadata.getItemTypeName(itemTypeId);
		if (!itemTypeName) {
			return null;
		}

		const url = aras.getScriptsURL(
			'itemsGrid.html?itemtypeID=' +
				itemTypeId +
				'&itemtypeName=' +
				itemTypeName +
				(favoriteSearchId ? '&favoriteSearchId=' + favoriteSearchId : '')
		);

		const winName = 'search_' + itemTypeId + itemTypeName + '_' + Date.now();

		const win = this.open(url, winName, false);
		win.addEventListener('resize', async () => {
			window.dispatchEvent(new CustomEvent('resize'));
			await this.render();
			this.scrollIntoView(this.selectedTab);
		});
		this.updateTitleTab(winName, {
			itemTypeId,
			favoriteSearchId,
			label: itemTypeName,
			image: icons.gridSearch
		});

		const itemTypePromise = itemTypeMetadata.getItemType(itemTypeId, 'id');
		itemTypePromise.then((itemType) => {
			const label = itemType['label_plural'];
			if (!label) {
				return;
			}

			const props = { label };
			return this.updateTitleTab(winName, props);
		});

		return win;
	}
	_hideAll() {
		this.tabs.forEach((id) => {
			const frame = document.getElementById(id);
			if (frame) {
				frame.style.opacity = '0';
				frame.style['z-index'] = '-1';
			}
		});
	}
	ejectTab(id: string) {
		const data = this.data.get(id);
		super.removeTab(id);
		data && this.options.updateTabState?.(this.tabs, id, data);
		return data;
	}
	async selectTab(id: string, preventFocus = false): Promise<boolean> {
		if (!id) {
			const previousSelectedId = this.selectedTab;
			this.selectedTab = null;
			const event = new CustomEvent('select', {
				detail: {
					id: this.selectedTab,
					previousId: previousSelectedId
				}
			});
			this.dispatchEvent(event);
			return false;
		}

		this._hideAll();
		const frame = document.getElementById(id);
		if (frame) {
			frame.style.opacity = '1';
			frame.style['z-index'] = 'auto';
		}
		if (!preventFocus) {
			this.focusTab(id);
		}

		return await super.selectTab(id);
	}
	focusTab(id: string) {
		const tabContainer = document.getElementById(id);
		const contentWindow = tabContainer.contentWindow;
		if (contentWindow?.nativeFocus) {
			contentWindow.nativeFocus();
		} else if (contentWindow?.focus) {
			contentWindow.focus();
		}
	}
	_getNextTabId() {
		const lastTabId = this.tabs[this.tabs.length - 1];
		if (lastTabId === this.selectedTab) {
			return this.tabs[this.tabs.length - 2] || null;
		}

		return lastTabId || null;
	}
	async removeTab(
		id: string,
		ignorePageCloseHooks = false
	): Promise<void | boolean> {
		const tabContainer = document.getElementById(id);
		if (!tabContainer) {
			return;
		}

		const remove = async () => {
			const tab = this.data.get(id);
			if (tab?.layout) {
				const layoutDestroyed = await tab.layout.tryDestroyLayout();
				if (!layoutDestroyed) {
					return;
				}
			}

			super.removeTab(tabContainer.id);
			tab && this.options.updateTabState?.(this.tabs, tabContainer.id, tab);
			const itemId = tabContainer.id.replace(aras.mainWindowName + '_', '');
			aras.uiUnregWindowEx(itemId);
			tabContainer.src = 'about:blank';
			tabContainer.parentNode.removeChild(tabContainer);
		};
		if (
			!tabContainer.contentWindow ||
			tabContainer.contentWindow.close.toString().indexOf('[native code]') > -1
		) {
			await remove();
			return;
		}

		return new Promise((resolve) => {
			tabContainer.contentWindow.close(async (isClosed: boolean) => {
				if (isClosed) {
					await remove();
				}
				resolve(isClosed);
			}, ignorePageCloseHooks);
		});
	}
	async clickOpenInTearOff(id) {
		if (!id) {
			return;
		}

		const win = document.getElementById(id).contentWindow;
		if (win.windowType) {
			return await this.openNonItemWindow(win, id);
		}

		const selectItem = win.item;
		const itemId = selectItem.getAttribute('id');
		const itemType = selectItem.getAttribute('type');
		const ignorePageCloseHooks = true;

		const isClosed = await this.removeTab(id, ignorePageCloseHooks);
		if (!isClosed) {
			return;
		}

		const gettedItem = aras.getItemById(itemType, itemId);
		const openInTearOff = true;
		return await aras.uiShowItemEx(gettedItem, 'tab view', openInTearOff);
	}
	openNonItemWindow(win, id) {
		const itemId = win.itemId;
		const itemTypeName = win.itemTypeName;
		const windowType = win.windowType;
		return this.removeTab(id).then(function (isClosed) {
			if (isClosed) {
				if (windowType === 'whereUsed' || windowType === 'structureBrowser') {
					window.Dependencies.view(
						itemTypeName,
						itemId,
						windowType === 'whereUsed',
						aras,
						true
					);
				}
			}
		});
	}
	async setTitleTabWithFrame(frameWindow: Window) {
		let itemTypeName;
		let item;

		if (!frameWindow.frameElement) {
			return;
		}

		if (frameWindow.item && frameWindow.itemTypeName) {
			item = frameWindow.item;
			itemTypeName = frameWindow.itemTypeName;
		} else {
			const win = window[frameWindow.paramObjectName];
			itemTypeName = window[frameWindow.paramObjectName].itemTypeName;
			item = win.item;
		}

		const winName = frameWindow.frameElement.getAttribute('id') || '';
		const itemType = await itemTypeMetadata.getItemType(itemTypeName);
		const itemId = item.getAttribute('id');
		const itemTypeId = itemTypeMetadata.getItemTypeId(itemTypeName);
		const image = itemType['open_icon'] ?? icons.defaultItemType;
		const label = aras.getKeyedNameEx(item);
		const props = { itemId, itemTypeId, image, label };
		return this.updateTitleTab(winName, props);
	}
	async setTabContent(id: string, props: Partial<HeaderTab>) {
		await super.setTabContent(id, props);
		this.options.updateTabState?.(this.tabs, id, props);
	}
	async updateTitleTab(id: string, props: Partial<HeaderTab>) {
		await this.setTabContent(id, props);
		this.scrollIntoView(id);
	}
	async addTab(id: string, options: Partial<HeaderTab>, position?: number) {
		await super.addTab(id, options, position);

		const data = this.data.get(id);
		data && this.options.updateTabState?.(this.tabs, id, data);
	}
	open(src: string, winName: string, isUnfocused: boolean, className = '') {
		const iframe = document.createElement('IFRAME');
		const isFirstTab = this.tabs.length === 0;
		document.getElementById('center')?.appendChild(iframe);
		this.addTab(winName, { closable: true });
		if (!winName.startsWith('innovator_')) {
			this.setTabContent(winName, { parentTab: null });
		}
		iframe.id = winName;
		iframe.className = `${this._frameCssClass} ${className || ''}`.trim();
		iframe.src = src;
		iframe.setAttribute('role', 'tabpanel');
		if (isUnfocused && !isFirstTab) {
			iframe.style.opacity = '0';
			iframe.style['z-index'] = '-1';
		} else {
			selectTab(this, winName, isUnfocused);
		}
		const win = iframe.contentWindow;
		win.opener = window;
		win.name = iframe.name = winName;
		return win;
	}
	dropdownTabRender() {
		const select = (event: MouseEvent) => {
			const target = event.target;
			const listTab = (target as HTMLElement).closest('li');
			const tabId = listTab?.getAttribute('list-data-id');

			if (tabId) {
				selectTab(this, tabId);
			}
		};

		const items = this.tabs.map(
			function (tab) {
				const data = this.data.get(tab);
				const icon = this._getImage(data.image);
				if (icon) {
					icon.className = 'aras-list-item__icon';
				}
				const className =
					'aras-list-item aras-list-item_iconed' +
					(this.selectedTab === tab ? ' selected' : '');

				return (
					<li className={className} list-data-id={tab}>
						<span className="condition-icon aras-icon-radio" />
						{icon}
						{data.label}
					</li>
				);
			}.bind(this)
		);
		const list = (
			<ul className="aras-list" onmousedown={select}>
				{items}
			</ul>
		);
		Inferno.render(list, this._dropdownBox);
	}
	updateTabInformation(currentID, newID) {
		const index = this.tabs.indexOf(currentID);
		if (index !== -1) {
			this.tabs[index] = newID;
		}

		const dataObj = this.data.get(currentID);
		this.data.delete(currentID);
		this.data.set(newID, Object.assign({}, dataObj));

		if (currentID === this.selectedTab) {
			this.selectedTab = newID;
		}

		const frame = document.getElementById(currentID);
		frame.id = frame.name = newID;
		this.render();
	}
	getSearchGridTabs(id) {
		return this.tabs
			.filter(function (item) {
				return item.indexOf('search_' + id) > -1;
			})
			.map(function (item) {
				return window.document.getElementById(item).contentWindow;
			});
	}
	render() {
		const isTabOpen = this.tabs.length > 0;
		this.classList.toggle('content-block__main-tabs_hidden', !isTabOpen);
		return super.render();
	}
	forceCloseAllTabs() {
		this.tabs.forEach((id) => {
			const tab = this.data.get(id);
			if (tab?.layout) {
				tab.layout.destroy();
				return;
			}

			this.data.delete(id);
			const frame = window.document.getElementById(id);
			const itemId = frame.id.replace(aras.mainWindowName + '_', '');
			aras.uiUnregWindowEx(itemId);
			frame.src = 'about:blank';
		});
		this.tabs = [];
	}
	async closeTabs(tabs = [...this.tabs]) {
		const editedTabs = [];
		const promisesArray = tabs.map((tabId) => {
			let isEdited = false;
			if (tabId.startsWith('innovator_')) {
				const tabWindow = window.document.getElementById(tabId).contentWindow;
				isEdited =
					tabWindow.aras &&
					(tabWindow.aras.isDirtyEx(tabWindow.item) ||
						tabWindow.aras.isTempEx(tabWindow.item));
			}

			if (isEdited) {
				editedTabs.push(tabId);
				return Promise.resolve();
			}

			return this.removeTab(tabId);
		});
		await Promise.all(promisesArray);

		for (let i = 0; i < editedTabs.length; i++) {
			const tabId = editedTabs[i];
			await this.selectTab(tabId);
			await this.removeTab(tabId);
		}

		return await this.render();
	}
	closeOtherTabs(excludedTabId) {
		const excludedTabIndex = this.tabs.indexOf(excludedTabId);
		const removableTabs = this.tabs.slice();
		removableTabs.splice(excludedTabIndex, 1);

		return this.closeTabs(removableTabs);
	}
}

HeaderTabs.define('aras-header-tabs');
